import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import AdvancedValuationModal from '../components/AdvancedValuationModal';
import { TrendingUp, DollarSign, X, BarChart3 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useBusinessContext } from '../contexts/BusinessContext';
import { usePermissions } from '../contexts/PermissionContext';
import ViewerBanner from '../components/ViewerBanner';
import { supabase } from '../lib/supabase';
import { useSimpleValuationInput } from '../hooks/useSimpleValuationInput';
import { METHOD_DESCRIPTIONS, QUIZ_QUESTIONS, FIELD_HELP_CONTENT } from '../utils/valuationConstants';
import { formatNumberWithCommas, parseNumberFromFormatted, formatCurrency as formatCurrencyUtil, getUserId } from '../utils/valuationUtils';
import FieldHelpModal from '../components/valuation/FieldHelpModal';
import EnterpriseValueModal from '../components/valuation/EnterpriseValueModal';
import DiscountInfoModal from '../components/valuation/DiscountInfoModal';
import MethodDescriptionsModal from '../components/valuation/MethodDescriptionsModal';
import ValuationQuizModal from '../components/valuation/ValuationQuizModal';
import QuizResultModal from '../components/valuation/QuizResultModal';
// Refactored components
import ValuationHeader from '../components/valuation/ValuationHeader';
import InfoCards from '../components/valuation/InfoCards';
import IndustrySelection from '../components/valuation/IndustrySelection';
import MethodSelection from '../components/valuation/MethodSelection';
import PrivateDiscountSlider from '../components/valuation/PrivateDiscountSlider';
import SubmitButtons from '../components/valuation/SubmitButtons';
import ValuationResults from '../components/valuation/ValuationResults';
import FinancialInputs from '../components/valuation/FinancialInputs';

const ValuationCalculator = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { currentBusinessId } = useBusinessContext();
  const { isViewer } = usePermissions();
  const { loadSimpleValuationInput, saveSimpleValuationInput } = useSimpleValuationInput(currentBusinessId);

  const [industries, setIndustries] = useState([]);
  const [selectedParent, setSelectedParent] = useState(() => {
    const userId = getUserId();
    return localStorage.getItem(`valuationSelectedParent_${userId}`) || '';
  });
  const [loading, setLoading] = useState(false);
  const [sliderCalculating, setSliderCalculating] = useState(false);
  const [error, setError] = useState('');
  const [advancedError, setAdvancedError] = useState('');
  const [result, setResult] = useState(() => {
    const userId = getUserId();
    const saved = localStorage.getItem(`valuationResult_${userId}`);
    return saved ? JSON.parse(saved) : null;
  });
  const [showEVModal, setShowEVModal] = useState(false);
  const [showMethodModal, setShowMethodModal] = useState(false);
  const [showQuiz, setShowQuiz] = useState(false);
  const [showQuizResult, setShowQuizResult] = useState(false);
  const [quizRecommendation, setQuizRecommendation] = useState(null);
  const [showDiscountInfo, setShowDiscountInfo] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);  // ← ADD THIS!
  const [helpContent, setHelpContent] = useState({ title: '', content: '' });  // ← AND THIS!
  const [showAdvancedModal, setShowAdvancedModal] = useState(false);
  const [advancedResult, setAdvancedResult] = useState(() => {
    const userId = getUserId();
    const saved = localStorage.getItem(`advancedValuationResult_${userId}`);
    return saved ? JSON.parse(saved) : null;
  });
  const [showAdvancedResultModal, setShowAdvancedResultModal] = useState(false);
  const [showCalculationModal, setShowCalculationModal] = useState(false);
  const [industrySearch, setIndustrySearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);

  const [formData, setFormData] = useState(() => {
    const userId = getUserId();
    const savedFormData = localStorage.getItem(`valuationFormData_${userId}`);
    if (savedFormData) {
      try {
        return JSON.parse(savedFormData);
      } catch (e) {
        console.error('Error parsing saved form data:', e);
      }
    }
    return {
      revenue: '',
      ebitda: '',
      net_income: '',
      sde: '',
      cash_flow: '',
      total_assets: '',
      total_liabilities: '',
      industry_id: '',
      method: 'cca',
      private_company_discount: 25,
      manual_multiple: '',
      manual_multiple_type: 'ev_ebitda',
      manual_industry_name: '',
      tax_rate_percent: '',
      tax_amount_dollar: '',
      nopat: '',
      dcf_discount_rate: 15,
      dcf_growth_rate: 3,
      capitalization_rate: '',
      capitalization_earnings_type: 'ebitda', // 'ebitda', 'net_income', or 'sde'
      dcf_years: 5,
      dcf_perpetuity: false,
      business_type: '',
      ebitda_input_type: 'dollar', // 'dollar' or 'percentage'
      ebitda_margin: '', // Store the percentage when using margin mode
      net_income_input_type: 'dollar', // 'dollar' or 'percentage'
      net_income_margin: '', // Store the percentage when using margin mode
      sde_input_type: 'dollar', // 'dollar' or 'percentage'
      sde_margin: '' // Store the percentage when using margin mode
    };
  });

  const debounceTimeout = useRef(null);
  const sliderDebounceTimeout = useRef(null); // Separate ref for slider calculations
  const [updateCounter, setUpdateCounter] = useState(0);

  const [quizAnswers, setQuizAnswers] = useState({
    businessStage: '',
    dataAvailable: '',
    industryType: '',
    exitTimeline: '',
    profitability: '',
    growthTrend: '',
    assetIntensity: '',
    industryComparables: '',
    futureProjections: '',
    valuationPurpose: ''
  });

  // Function to show help modal
  const showHelp = (field) => {
    setHelpContent(FIELD_HELP_CONTENT[field]);
    setShowHelpModal(true);
  };

  useEffect(() => {
    fetchIndustries();

    return () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
      if (sliderDebounceTimeout.current) {
        clearTimeout(sliderDebounceTimeout.current);
      }
    };
  }, []);

  // Reload data when user logs in/changes
  useEffect(() => {
    if (user && user.id) {
      const userId = user.id;

      // Load saved form data
      const savedFormData = localStorage.getItem(`valuationFormData_${userId}`);
      if (savedFormData) {
        try {
          setFormData(JSON.parse(savedFormData));
        } catch (e) {
          console.error('Error loading form data:', e);
        }
      }

      // Load saved results
      const savedResult = localStorage.getItem(`valuationResult_${userId}`);
      if (savedResult) {
        try {
          setResult(JSON.parse(savedResult));
        } catch (e) {
          console.error('Error loading result:', e);
        }
      }

      // Load saved advanced result
      const savedAdvancedResult = localStorage.getItem(`advancedValuationResult_${userId}`);
      if (savedAdvancedResult) {
        try {
          setAdvancedResult(JSON.parse(savedAdvancedResult));
        } catch (e) {
          console.error('Error loading advanced result:', e);
        }
      }

      // Load saved parent
      const savedParent = localStorage.getItem(`valuationSelectedParent_${userId}`);
      if (savedParent) {
        setSelectedParent(savedParent);
      }
    }
  }, [user]); // Re-run when user changes

  // Save result to localStorage when it changes
  useEffect(() => {
    if (result) {
      const userId = getUserId();
      localStorage.setItem(`valuationResult_${userId}`, JSON.stringify(result));
    }
  }, [result]);

  // Save advanced result to localStorage when it changes
  useEffect(() => {
    if (advancedResult) {
      const userId = getUserId();
      localStorage.setItem(`advancedValuationResult_${userId}`, JSON.stringify(advancedResult));
    }
  }, [advancedResult]);

  // Save selected parent to localStorage when it changes
  useEffect(() => {
    if (selectedParent) {
      const userId = getUserId();
      localStorage.setItem(`valuationSelectedParent_${userId}`, selectedParent);
    }
  }, [selectedParent]);

  // Save entire formData to localStorage when it changes
  useEffect(() => {
    const userId = getUserId();
    localStorage.setItem(`valuationFormData_${userId}`, JSON.stringify(formData));
  }, [formData]);

  // Load simple valuation input data from database when business changes
  useEffect(() => {
    const loadData = async () => {
      if (!currentBusinessId) return;

      try {
        const data = await loadSimpleValuationInput();
        if (data) {
          // Merge database data with formData, prioritizing database values
          setFormData(prevFormData => ({
            ...prevFormData,
            revenue: data.revenue || prevFormData.revenue,
            ebitda: data.ebitda || prevFormData.ebitda,
            net_income: data.net_income || prevFormData.net_income,
            sde: data.sde || prevFormData.sde,
            cash_flow: data.cash_flow || prevFormData.cash_flow,
            total_assets: data.total_assets || prevFormData.total_assets,
            total_liabilities: data.total_liabilities || prevFormData.total_liabilities,
            industry_id: data.industry_id || prevFormData.industry_id,
            method: data.method || prevFormData.method,
            private_company_discount: data.private_company_discount !== null ? data.private_company_discount : prevFormData.private_company_discount,
            business_type: data.business_type || prevFormData.business_type,
            manual_multiple: data.manual_multiple || prevFormData.manual_multiple,
            manual_multiple_type: data.manual_multiple_type || prevFormData.manual_multiple_type,
            manual_industry_name: data.manual_industry_name || prevFormData.manual_industry_name,
            tax_rate_percent: data.tax_rate_percent || prevFormData.tax_rate_percent,
            tax_amount_dollar: data.tax_amount_dollar || prevFormData.tax_amount_dollar,
            nopat: data.nopat || prevFormData.nopat,
            dcf_discount_rate: data.dcf_discount_rate !== null ? data.dcf_discount_rate : prevFormData.dcf_discount_rate,
            dcf_growth_rate: data.dcf_growth_rate !== null ? data.dcf_growth_rate : prevFormData.dcf_growth_rate,
            dcf_years: data.dcf_years || prevFormData.dcf_years,
            dcf_perpetuity: data.dcf_perpetuity !== null ? data.dcf_perpetuity : prevFormData.dcf_perpetuity,
            capitalization_rate: data.capitalization_rate || prevFormData.capitalization_rate,
            capitalization_earnings_type: data.capitalization_earnings_type || prevFormData.capitalization_earnings_type,
            ebitda_input_type: data.ebitda_input_type || prevFormData.ebitda_input_type,
            ebitda_margin: data.ebitda_margin || prevFormData.ebitda_margin,
            net_income_input_type: data.net_income_input_type || prevFormData.net_income_input_type,
            net_income_margin: data.net_income_margin || prevFormData.net_income_margin,
            sde_input_type: data.sde_input_type || prevFormData.sde_input_type,
            sde_margin: data.sde_margin || prevFormData.sde_margin
          }));
        }
      } catch (error) {
        console.error('[SimpleValuation] Error loading data from database:', error);
      }
    };

    loadData();
  }, [currentBusinessId, loadSimpleValuationInput]);

  // Save formData to database with debouncing
  useEffect(() => {
    if (!currentBusinessId) return;

    // Clear any existing timeout
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }

    // Set a new timeout to save after 2 seconds of inactivity
    debounceTimeout.current = setTimeout(async () => {
      try {
        // Prepare data for saving
        const dataToSave = {
          revenue: parseNumberFromFormatted(formData.revenue) || null,
          ebitda: parseNumberFromFormatted(formData.ebitda) || null,
          net_income: parseNumberFromFormatted(formData.net_income) || null,
          sde: parseNumberFromFormatted(formData.sde) || null,
          cash_flow: parseNumberFromFormatted(formData.cash_flow) || null,
          total_assets: parseNumberFromFormatted(formData.total_assets) || null,
          total_liabilities: parseNumberFromFormatted(formData.total_liabilities) || null,
          industry_id: formData.industry_id ? parseInt(formData.industry_id) : null,
          method: formData.method,
          private_company_discount: formData.private_company_discount,
          business_type: formData.business_type,
          manual_multiple: formData.manual_multiple ? parseFloat(formData.manual_multiple) : null,
          manual_multiple_type: formData.manual_multiple_type,
          manual_industry_name: formData.manual_industry_name,
          tax_rate_percent: formData.tax_rate_percent ? parseFloat(formData.tax_rate_percent) : null,
          tax_amount_dollar: formData.tax_amount_dollar ? parseFloat(formData.tax_amount_dollar) : null,
          nopat: formData.nopat ? parseFloat(formData.nopat) : null,
          dcf_discount_rate: formData.dcf_discount_rate,
          dcf_growth_rate: formData.dcf_growth_rate,
          dcf_years: formData.dcf_years,
          dcf_perpetuity: formData.dcf_perpetuity,
          capitalization_rate: formData.capitalization_rate ? parseFloat(formData.capitalization_rate) : null,
          capitalization_earnings_type: formData.capitalization_earnings_type,
          ebitda_input_type: formData.ebitda_input_type,
          ebitda_margin: formData.ebitda_margin ? parseFloat(formData.ebitda_margin) : null,
          net_income_input_type: formData.net_income_input_type,
          net_income_margin: formData.net_income_margin ? parseFloat(formData.net_income_margin) : null,
          sde_input_type: formData.sde_input_type,
          sde_margin: formData.sde_margin ? parseFloat(formData.sde_margin) : null
        };

        const saved = await saveSimpleValuationInput(dataToSave);
        if (saved) {
        }
      } catch (error) {
        console.error('[SimpleValuation] Error auto-saving data:', error);
      }
    }, 2000); // 2 second debounce

    // Cleanup function
    return () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
    };
  }, [formData, currentBusinessId, saveSimpleValuationInput]);

  // Restore industry search text and selected parent when industries are loaded
  useEffect(() => {
    if (industries.length > 0 && formData.industry_id) {
      const selectedIndustry = industries.find(ind => ind.id === parseInt(formData.industry_id));
      if (selectedIndustry) {
        setIndustrySearch(`${selectedIndustry.industry_name} (${selectedIndustry.business_type === 'private' ? 'Private' : 'Public'})`);
        if (selectedIndustry.industry_parent && !selectedParent) {
          setSelectedParent(selectedIndustry.industry_parent);
        }
      }
    }
  }, [industries, formData.industry_id]);

  // Close search dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is outside the search container
      const searchContainer = event.target.closest('.industry-search-container');
      if (!searchContainer && showSearchDropdown) {
        setShowSearchDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSearchDropdown]);

  // Get authentication token - supports both Supabase and Flask auth
  const getAuthToken = async () => {
    // Check for Flask token first
    const flaskToken = localStorage.getItem('token');
    if (flaskToken) {
      return flaskToken;
    }

    // Check for Supabase session
    try {
      const { data: { session } } = await supabase.auth.getSession();

      if (session?.access_token) {
        return session.access_token;
      }
    } catch (err) {
      console.error('[AUTH DEBUG] Error getting Supabase session:', err);
    }

    return null;
  };

  // Check if token is valid - supports both Supabase and Flask auth
  const checkToken = async () => {
    const token = await getAuthToken();
    if (token) {
      return true;
    }

    // No valid authentication found
    setError('Session expired. Please log in again.');
    setTimeout(() => {
      window.location.href = '/login';
    }, 2000);
    return false;
  };

  const fetchIndustries = async () => {
    try {
      if (!(await checkToken())) return;

      const response = await api.get('/valuation/industries');
      setIndustries(response.data.industries);
    } catch (err) {
      console.error('Error fetching industries:', err);
      if (err.response?.status === 401) {
        setError('Session expired. Please log in again.');
        setTimeout(() => {
          window.location.href = '/login';
        }, 2000);
      } else {
        setError('Failed to load industries');
      }
    }
  };

  const handleIndustrySearch = (searchTerm) => {
    setIndustrySearch(searchTerm);

    if (searchTerm.length < 2) {
      setSearchResults([]);
      setShowSearchDropdown(false);
      return;
    }

    // Search through all industries
    const results = industries.filter(industry =>
      industry.industry_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      industry.naics_code.includes(searchTerm)
    ).slice(0, 10); // Limit to 10 results

    setSearchResults(results);
    setShowSearchDropdown(results.length > 0);
  };

  const selectIndustryFromSearch = (industry) => {
    // Set the parent category and specific industry
    setSelectedParent(industry.industry_parent || '');
    setFormData(prev => ({ ...prev, industry_id: industry.id.toString() }));
    setIndustrySearch(`${industry.industry_name} (${industry.business_type === 'private' ? 'Private' : 'Public'})`);
    setShowSearchDropdown(false);
  };

  const handleQuizAnswer = (questionId, value, method) => {
    setQuizAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  const calculateQuizResult = () => {
    const methodCounts = {};

    QUIZ_QUESTIONS.forEach(question => {
      const answer = quizAnswers[question.id];
      const option = question.options.find(opt => opt.value === answer);
      if (option) {
        methodCounts[option.method] = (methodCounts[option.method] || 0) + 1;
      }
    });

    // Find the method with the highest count
    let recommendedMethod = 'cca'; // Default to CCA if no answers
    let maxCount = 0;

    Object.entries(methodCounts).forEach(([method, count]) => {
      if (count > maxCount) {
        maxCount = count;
        recommendedMethod = method;
      }
    });

    setFormData(prev => ({ ...prev, method: recommendedMethod }));
    setQuizRecommendation(METHOD_DESCRIPTIONS[recommendedMethod]);
    setShowQuiz(false);
    setShowQuizResult(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Strip commas from numeric inputs
    const cleanValue = parseNumberFromFormatted(value);

    // Recalculate tax and NOPAT when EBIT changes
    if (name === 'ebit' && cleanValue) {
      const ebit = parseFloat(cleanValue);
      if (formData.tax_rate_percent) {
        const taxRate = parseFloat(formData.tax_rate_percent) / 100;
        setFormData(prev => ({
          ...prev,
          [name]: cleanValue,
          tax_amount_dollar: (ebit * taxRate).toFixed(2),
          nopat: (ebit * (1 - taxRate)).toFixed(2)
        }));
        return;
      } else if (formData.tax_amount_dollar) {
        const taxAmount = parseFloat(parseNumberFromFormatted(formData.tax_amount_dollar));
        setFormData(prev => ({
          ...prev,
          [name]: cleanValue,
          tax_rate_percent: ((taxAmount / ebit) * 100).toFixed(2),
          nopat: (ebit - taxAmount).toFixed(2)
        }));
        return;
      }
    }

    setFormData(prev => ({
      ...prev,
      [name]: cleanValue || value // Use cleanValue for numbers, original value for other fields
    }));
  };

  const handleParentChange = (e) => {
    const newParent = e.target.value;
    setSelectedParent(newParent);

    // Reset industry selection and manual industry name when parent changes
    const updates = {
      industry_id: '',
      manual_industry_name: ''
    };

    // If switching to manual industry and CCA is selected, switch to DCF
    if (newParent === 'MANUAL' && formData.method === 'cca') {
      updates.method = 'dcf';
    }

    setFormData(prev => ({
      ...prev,
      ...updates
    }));
  };

  const canCalculate = () => {
    const hasIndustry = selectedParent === 'MANUAL'
      ? formData.manual_industry_name
      : formData.industry_id;
    return formData.revenue && formData.ebitda && hasIndustry;
  };

  const handleDiscountChange = (e) => {
    const newDiscount = parseInt(e.target.value);


    setFormData(prev => ({
      ...prev,
      private_company_discount: newDiscount
    }));

    // If we already have a result, we can recalculate regardless of canCalculate()
    // because we're just adjusting the discount on an existing calculation
    if (result) {
      if (sliderDebounceTimeout.current) {
        clearTimeout(sliderDebounceTimeout.current);
      }

      setSliderCalculating(true);

      const timeoutId = setTimeout(() => {
        performCalculation(newDiscount);
      }, 800);

      sliderDebounceTimeout.current = timeoutId;
    } else {
    }
  };

  const performCalculation = async (customDiscount = null) => {
    const discount = customDiscount !== null ? customDiscount : formData.private_company_discount;
    const isSliderRecalculation = customDiscount !== null;


    // Always get fresh token right before the API call (supports both auth methods)
    const token = await getAuthToken();
    if (!token) {
      setError('Session expired. Please log in again.');
      setTimeout(() => {
        window.location.href = '/login';
      }, 2000);
      setLoading(false);
      if (isSliderRecalculation) {
        setSliderCalculating(false);
      }
      return;
    }

    try {
      const payload = {
        revenue: parseNumberFromFormatted(formData.revenue) || 0,
        ebitda: parseNumberFromFormatted(formData.ebitda) || 0,
        net_income: parseNumberFromFormatted(formData.net_income) || 0,
        sde: parseNumberFromFormatted(formData.sde) || 0,
        ebit: parseNumberFromFormatted(formData.ebit) || 0,
        ebit_after_tax: parseNumberFromFormatted(formData.ebit_after_tax) || 0,
        nopat: parseNumberFromFormatted(formData.nopat) || parseNumberFromFormatted(formData.ebit_after_tax) || 0,
        tax_rate: parseNumberFromFormatted(formData.tax_rate_percent) || 0,
        cash_flow: parseNumberFromFormatted(formData.cash_flow) || (parseNumberFromFormatted(formData.ebitda) * 0.8) || 0,
        total_assets: parseNumberFromFormatted(formData.total_assets) || 0,
        total_liabilities: parseNumberFromFormatted(formData.total_liabilities) || 0,
        industry_id: parseInt(formData.industry_id) || null,
        method: formData.method,
        private_company_discount: discount / 100,
        cca_multiple_type: formData.cca_multiple_type || 'ev_ebitda',
        manual_multiple: parseNumberFromFormatted(formData.manual_multiple) || 0,
        manual_multiple_type: formData.manual_multiple_type,
        manual_industry_name: formData.manual_industry_name || null,
        discount_rate: parseNumberFromFormatted(formData.dcf_discount_rate) / 100 || 0.15,
        dcf_annual_growth_rate: parseNumberFromFormatted(formData.dcf_annual_growth_rate) / 100 || 0.05,
        dcf_growth_rate: parseNumberFromFormatted(formData.dcf_growth_rate) / 100 || 0.03,
        capitalization_rate: parseNumberFromFormatted(formData.capitalization_rate) / 100 || null,
        capitalization_earnings_type: formData.capitalization_earnings_type || 'ebitda',
        dcf_years: formData.dcf_perpetuity ? null : (parseInt(formData.dcf_years) || 5),
        dcf_perpetuity: formData.dcf_perpetuity || false
      };

      const response = await api.post('/valuation/calculate', payload);


      const newResult = {
        ...response.data,
        timestamp: Date.now()
      };

      setResult(newResult);
      setUpdateCounter(prev => prev + 1);
      setError('');

      // Auto-calculate profit gap after successful valuation
      try {
        await api.post('/profit-gap/auto-calculate', {
          industry_id: parseInt(formData.industry_id)
        });
      } catch (profitGapError) {
        // Silently fail - profit gap is optional
      }

      // Clear slider calculating state for slider recalculations (success case)
      if (isSliderRecalculation) {
        setSliderCalculating(false);
      }
      setLoading(false);
    } catch (err) {
      console.error('Valuation error:', err);
      console.error('Error response:', err.response);
      console.error('Error status:', err.response?.status);
      console.error('Error data:', err.response?.data);
      console.error('Valuation method was:', formData.method);

      if (err.response?.status === 401) {
        console.error('401 Unauthorized - Token may be expired or invalid');
        setError('Session expired. Please log in again.');
        // Clear invalid token
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setTimeout(() => {
          window.location.href = '/login';
        }, 2000);
      } else {
        setError(err.response?.data?.error || 'Failed to calculate valuation');
      }

      // Clear slider calculating state for slider recalculations (error case)
      if (isSliderRecalculation) {
        setSliderCalculating(false);
      }
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    await performCalculation();
  };

  const handleAdvancedValuation = async (financialData) => {
    setLoading(true);
    setAdvancedError('');

    try {
      const token = await getAuthToken();
      if (!token) {
        setAdvancedError('Session expired. Please log in again.');
        setTimeout(() => {
          window.location.href = '/login';
        }, 2000);
        setLoading(false);
        return;
      }

      const response = await api.post('/valuation/advanced', financialData);

      // Store the advanced result and keep the modal open to show results in the Results tab
      setAdvancedResult(response.data);
      setAdvancedError(''); // Clear any previous errors on success

    } catch (err) {
      console.error('Advanced valuation error:', err);
      if (err.response?.status === 401) {
        setAdvancedError('Session expired. Please log in again.');
        setTimeout(() => {
          window.location.href = '/login';
        }, 2000);
      } else {
        setAdvancedError(err.response?.data?.error || 'Failed to calculate advanced valuation');
      }
    } finally {
      setLoading(false);
    }
  };

  // Use imported formatCurrency utility
  const formatCurrency = formatCurrencyUtil;

  const selectedIndustry = industries.find(ind => ind.id === parseInt(formData.industry_id));

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Viewer Banner */}
      <ViewerBanner />

      {/* Header */}
      <ValuationHeader />

      {/* Info Cards */}
      <InfoCards
        onShowEVModal={() => setShowEVModal(true)}
        onShowQuiz={() => setShowQuiz(true)}
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Input Form */}
        <div className="lg:col-span-2">
          <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-lg border border-gray-200 p-6">
            {/* Industry Selection */}
            <IndustrySelection
              industries={industries}
              industrySearch={industrySearch}
              searchResults={searchResults}
              showSearchDropdown={showSearchDropdown}
              selectedParent={selectedParent}
              formData={formData}
              onIndustrySearchChange={handleIndustrySearch}
              onSelectIndustryFromSearch={selectIndustryFromSearch}
              onParentChange={handleParentChange}
              onInputChange={handleInputChange}
              onDropdownClose={() => setShowSearchDropdown(false)}
            />

            {/* Valuation Method */}
            <MethodSelection
              selectedParent={selectedParent}
              formData={formData}
              onInputChange={handleInputChange}
              onShowMethodModal={setShowMethodModal}
              industries={industries}
            />

            {/* Private Company Discount Slider */}
            <PrivateDiscountSlider
              discount={formData.private_company_discount}
              onDiscountChange={handleDiscountChange}
              onShowDiscountInfo={setShowDiscountInfo}
              sliderCalculating={sliderCalculating}
              result={result}
            />

            {/* Financial Inputs - Dynamic based on selected method */}
            <div className="mb-6 p-4 bg-gradient-to-br from-orange-50 to-amber-50 border-2 border-orange-200 rounded-lg">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-800">Financial Information</h3>
              </div>
              <FinancialInputs
                method={formData.method}
                formData={formData}
                onInputChange={handleInputChange}
                onShowHelp={showHelp}
                setFormData={setFormData}
                formatNumberWithCommas={formatNumberWithCommas}
              />
            </div>

            {/* Error Message */}
            {error && (
              <div className="mb-6 p-4 bg-red-50 border-2 border-red-300 rounded-lg">
                <p className="text-red-800 text-sm font-medium">{error}</p>
              </div>
            )}

            {/* Submit and Advanced Buttons */}
            <SubmitButtons
              loading={loading}
              result={result}
              onShowAdvancedModal={() => setShowAdvancedModal(true)}
              isViewer={isViewer}
            />
          </form>
        </div>

        {/* Results Panel */}
        <div className="lg:col-span-1" key={updateCounter}>
          <ValuationResults
            result={result}
            updateCounter={updateCounter}
            sliderCalculating={sliderCalculating}
            formData={formData}
            selectedIndustry={selectedIndustry}
            formatCurrency={formatCurrency}
            onShowCalculationModal={() => setShowCalculationModal(true)}
            onNavigateToDashboard={() => navigate('/valuation-dashboard')}
            onStartNewCalculation={() => window.location.reload()}
          />
        </div>
      </div>

      {/* Enterprise Value Modal */}
      <EnterpriseValueModal
        isOpen={showEVModal}
        onClose={() => setShowEVModal(false)}
        onSelectMethod={(method) => {
          setFormData(prev => ({ ...prev, method }));
          setShowEVModal(false);
        }}
        onStartQuiz={() => {
          setShowEVModal(false);
          setShowQuiz(true);
        }}
      />

      {/* Private Company Discount Info Modal */}
      <DiscountInfoModal
        isOpen={showDiscountInfo}
        onClose={() => setShowDiscountInfo(false)}
      />

      {/* Method Descriptions Modal */}
      <MethodDescriptionsModal
        isOpen={showMethodModal}
        onClose={() => setShowMethodModal(false)}
        onSelectMethod={(method) => {
          setFormData(prev => ({ ...prev, method }));
          setShowMethodModal(false);
        }}
      />

      {/* Quiz Modal */}
      <ValuationQuizModal
        isOpen={showQuiz}
        onClose={() => setShowQuiz(false)}
        quizAnswers={quizAnswers}
        onAnswerChange={handleQuizAnswer}
        onCalculateResult={calculateQuizResult}
      />

      {/* Quiz Result Modal */}
      <QuizResultModal
        isOpen={showQuizResult}
        onClose={() => setShowQuizResult(false)}
        quizRecommendation={quizRecommendation}
      />

      {/* Field Help Modal */}
      <FieldHelpModal
        isOpen={showHelpModal}
        onClose={() => setShowHelpModal(false)}
        helpContent={helpContent}
      />

      {/* Advanced Valuation Modal */}
      <AdvancedValuationModal
        isOpen={showAdvancedModal}
        onClose={() => {
          setShowAdvancedModal(false);
          setAdvancedResult(null);
          setAdvancedError(''); // Clear error when closing
        }}
        onSubmit={handleAdvancedValuation}
        valuationResult={advancedResult}
        error={advancedError}
        loading={loading}
      />

      {/* Advanced Results Modal */}
      {showAdvancedResultModal && advancedResult && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-green-600 to-emerald-600 flex-shrink-0">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-2xl font-bold text-white">Advanced Median Business Value Results</h2>
                  <p className="text-green-100 text-sm">Multi-year comprehensive analysis</p>
                </div>
                <button
                  onClick={() => setShowAdvancedResultModal(false)}
                  className="text-white hover:bg-white/20 p-2 rounded-lg"
                >
                  <X size={24} />
                </button>
              </div>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {/* Primary Valuation - Selected Method */}
              {advancedResult.valuation_methods && advancedResult.selected_method && advancedResult.valuation_methods[advancedResult.selected_method] && (
                <div className="mb-6 bg-gradient-to-br from-emerald-50 via-green-50 to-teal-50 border-3 border-emerald-400 rounded-xl p-6 shadow-lg">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="bg-emerald-600 text-white rounded-full p-2">
                      <TrendingUp size={28} />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-emerald-900">
                        Your Median Business Value
                      </h3>
                      <p className="text-sm text-emerald-700">
                        {advancedResult.valuation_methods[advancedResult.selected_method].description}
                      </p>
                    </div>
                  </div>
                  <p className="text-5xl font-bold text-emerald-600 mb-2">
                    {new Intl.NumberFormat('en-US', {
                      style: 'currency',
                      currency: 'USD',
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0,
                    }).format(advancedResult.valuation_methods[advancedResult.selected_method].value)}
                  </p>
                  <p className="text-sm text-emerald-800 font-medium">
                    Using {advancedResult.selected_method.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())} Method
                  </p>
                </div>
              )}

              {/* Growth Analysis */}
              {advancedResult.growth_metrics && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-3">Growth Analysis</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-gradient-to-br from-blue-50 to-blue-100 border-2 border-blue-300 rounded-lg p-4 shadow-sm">
                      <p className="text-sm text-blue-800 font-medium mb-1">Revenue CAGR</p>
                      <p className="text-3xl font-bold text-blue-600">
                        {advancedResult.growth_metrics.revenue_cagr.toFixed(1)}%
                      </p>
                      <p className="text-xs text-blue-600 mt-1">Compound Annual Growth Rate</p>
                    </div>
                    <div className="bg-gradient-to-br from-purple-50 to-purple-100 border-2 border-purple-300 rounded-lg p-4 shadow-sm">
                      <p className="text-sm text-purple-800 font-medium mb-1">EBITDA CAGR</p>
                      <p className="text-3xl font-bold text-purple-600">
                        {advancedResult.growth_metrics.ebitda_cagr.toFixed(1)}%
                      </p>
                      <p className="text-xs text-purple-600 mt-1">Profitability Growth</p>
                    </div>
                    <div className="bg-gradient-to-br from-amber-50 to-amber-100 border-2 border-amber-300 rounded-lg p-4 shadow-sm">
                      <p className="text-sm text-amber-800 font-medium mb-1">SDE CAGR</p>
                      <p className="text-3xl font-bold text-amber-600">
                        {advancedResult.growth_metrics.sde_cagr.toFixed(1)}%
                      </p>
                      <p className="text-xs text-amber-600 mt-1">Seller's Discretionary Earnings</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Financial Summary - Enhanced */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-3">Financial Summary</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-gradient-to-br from-slate-50 to-slate-100 border-2 border-slate-300 rounded-lg p-5 shadow-sm">
                    <h4 className="font-semibold text-slate-800 mb-3 text-md flex items-center gap-2">
                      <DollarSign size={18} className="text-slate-600" />
                      Current Year ({new Date().getFullYear()})
                    </h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between items-center py-1.5 border-b border-slate-200">
                        <span className="text-slate-700">Revenue:</span>
                        <span className="font-bold text-slate-900">
                          {new Intl.NumberFormat('en-US', {
                            style: 'currency',
                            currency: 'USD',
                            minimumFractionDigits: 0,
                          }).format(advancedResult.financial_summary.current_year.revenue)}
                        </span>
                      </div>
                      <div className="flex justify-between items-center py-1.5 border-b border-slate-200">
                        <span className="text-slate-700">EBITDA:</span>
                        <span className="font-bold text-slate-900">
                          {new Intl.NumberFormat('en-US', {
                            style: 'currency',
                            currency: 'USD',
                            minimumFractionDigits: 0,
                          }).format(advancedResult.financial_summary.current_year.ebitda)}
                        </span>
                      </div>
                      <div className="flex justify-between items-center py-1.5 border-b border-slate-200">
                        <span className="text-slate-700">SDE:</span>
                        <span className="font-bold text-slate-900">
                          {new Intl.NumberFormat('en-US', {
                            style: 'currency',
                            currency: 'USD',
                            minimumFractionDigits: 0,
                          }).format(advancedResult.financial_summary.current_year.sde)}
                        </span>
                      </div>
                      <div className="flex justify-between items-center py-1.5">
                        <span className="text-slate-700">EBITDA Margin:</span>
                        <span className="font-bold text-emerald-600">
                          {advancedResult.financial_summary.current_year.ebitda_margin.toFixed(1)}%
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 border-2 border-indigo-300 rounded-lg p-5 shadow-sm">
                    <h4 className="font-semibold text-indigo-800 mb-3 text-md flex items-center gap-2">
                      <BarChart3 size={18} className="text-indigo-600" />
                      Multi-Year Average
                    </h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between items-center py-1.5 border-b border-indigo-200">
                        <span className="text-indigo-700">Avg Revenue:</span>
                        <span className="font-bold text-indigo-900">
                          {new Intl.NumberFormat('en-US', {
                            style: 'currency',
                            currency: 'USD',
                            minimumFractionDigits: 0,
                          }).format(advancedResult.financial_summary.averages.revenue)}
                        </span>
                      </div>
                      <div className="flex justify-between items-center py-1.5 border-b border-indigo-200">
                        <span className="text-indigo-700">Avg EBITDA:</span>
                        <span className="font-bold text-indigo-900">
                          {new Intl.NumberFormat('en-US', {
                            style: 'currency',
                            currency: 'USD',
                            minimumFractionDigits: 0,
                          }).format(advancedResult.financial_summary.averages.ebitda)}
                        </span>
                      </div>
                      <div className="flex justify-between items-center py-1.5 border-b border-indigo-200">
                        <span className="text-indigo-700">Avg SDE:</span>
                        <span className="font-bold text-indigo-900">
                          {new Intl.NumberFormat('en-US', {
                            style: 'currency',
                            currency: 'USD',
                            minimumFractionDigits: 0,
                          }).format(advancedResult.financial_summary.averages.sde)}
                        </span>
                      </div>
                      <div className="flex justify-between items-center py-1.5">
                        <span className="text-indigo-700">Avg EBITDA Margin:</span>
                        <span className="font-bold text-emerald-600">
                          {advancedResult.financial_summary.averages.ebitda_margin.toFixed(1)}%
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* Industry Info */}
              {advancedResult.industry_info && (
                <div className="mb-6 bg-gradient-to-br from-violet-50 to-purple-100 border-2 border-violet-300 rounded-lg p-5 shadow-sm">
                  <h3 className="text-lg font-semibold text-violet-900 mb-3 flex items-center gap-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                    Industry: {advancedResult.industry_info.name}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-white rounded-lg p-3 border border-violet-200">
                      <p className="text-xs text-violet-700 font-medium mb-2">Original Multiples (Public Companies):</p>
                      <div className="space-y-1 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">EV/EBITDA:</span>
                          <span className="font-bold text-gray-800">{advancedResult.industry_info.original_multiples.ev_ebitda.toFixed(1)}x</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">EV/Revenue:</span>
                          <span className="font-bold text-gray-800">{advancedResult.industry_info.original_multiples.ev_revenue.toFixed(1)}x</span>
                        </div>
                        {advancedResult.industry_info.ebitda_margin != null && (
                          <div className="flex justify-between">
                            <span className="text-gray-600">EBITDA Margin:</span>
                            <span className="font-bold text-gray-800">{advancedResult.industry_info.ebitda_margin.toFixed(1)}%</span>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="bg-white rounded-lg p-3 border border-violet-200">
                      <p className="text-xs text-violet-700 font-medium mb-2">
                        Adjusted for Private Company ({advancedResult.industry_info.private_discount_applied.toFixed(0)}% discount):
                      </p>
                      <div className="space-y-1 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">EV/EBITDA:</span>
                          <span className="font-bold text-violet-700">{advancedResult.industry_info.adjusted_multiples.ev_ebitda.toFixed(1)}x</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">EV/Revenue:</span>
                          <span className="font-bold text-violet-700">{advancedResult.industry_info.adjusted_multiples.ev_revenue.toFixed(1)}x</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Alternative Valuation Methods */}
              {advancedResult.valuation_methods && Object.keys(advancedResult.valuation_methods).length > 1 && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-3">Alternative Valuation Methods</h3>
                  <div className="space-y-3">
                    {Object.entries(advancedResult.valuation_methods)
                      .filter(([method]) => method !== advancedResult.selected_method)
                      .map(([method, data]) => (
                        <div key={method} className="bg-gradient-to-r from-gray-50 to-slate-50 border-2 border-gray-300 rounded-lg p-4 shadow-sm">
                          <div className="flex justify-between items-center">
                            <div className="flex-1">
                              <h4 className="font-semibold text-gray-800 text-md">
                                {method.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                              </h4>
                              <p className="text-sm text-gray-600 mt-1">{data.description}</p>
                            </div>
                            <div className="text-right ml-4">
                              <p className="text-2xl font-bold text-gray-700">
                                {new Intl.NumberFormat('en-US', {
                                  style: 'currency',
                                  currency: 'USD',
                                  minimumFractionDigits: 0,
                                }).format(data.value)}
                              </p>
                              {advancedResult.method_weights && advancedResult.method_weights[method] && (
                                <p className="text-xs text-gray-500 mt-1">
                                  Weight: {(advancedResult.method_weights[method] * 100).toFixed(0)}%
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              )}

              {/* Weighted Average Valuation */}
              {advancedResult.weighted_valuation && (
                <div className="mb-6 bg-gradient-to-r from-cyan-50 to-blue-100 border-2 border-cyan-400 rounded-lg p-6 shadow-sm">
                  <h3 className="text-lg font-semibold text-cyan-900 mb-2 flex items-center gap-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                    Weighted Average Median Value
                  </h3>
                  <p className="text-3xl font-bold text-cyan-700 mb-2">
                  {new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: 'USD',
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                  }).format(advancedResult.weighted_valuation)}
                  </p>
                  <div className="flex gap-4 text-sm text-cyan-700">
                    <span>
                      Range: {new Intl.NumberFormat('en-US', {
                        style: 'currency',
                        currency: 'USD',
                        minimumFractionDigits: 0,
                      }).format(advancedResult.valuation_range.low)}
                    </span>
                    <span>to</span>
                    <span>
                      {new Intl.NumberFormat('en-US', {
                        style: 'currency',
                        currency: 'USD',
                        minimumFractionDigits: 0,
                      }).format(advancedResult.valuation_range.high)}
                    </span>
                  </div>
                  <p className="text-xs text-cyan-600 mt-2">
                    Blended valuation across all methods weighted by reliability
                  </p>
                </div>
              )}

              {/* Key Insights */}
              {advancedResult.insights && advancedResult.insights.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-3">Key Insights</h3>
                  <div className="space-y-2">
                    {advancedResult.insights.map((insight, idx) => (
                      <div
                        key={idx}
                        className={`p-4 rounded-lg border-2 shadow-sm ${insight.type === 'positive'
                            ? 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-300'
                            : insight.type === 'warning'
                              ? 'bg-gradient-to-r from-yellow-50 to-amber-50 border-yellow-300'
                              : 'bg-gradient-to-r from-red-50 to-rose-50 border-red-300'
                          }`}
                      >
                        <div className="flex items-start gap-3">
                          <span className="text-lg font-bold">
                            {insight.type === 'positive' ? '✓' : insight.type === 'warning' ? '⚠' : '✗'}
                          </span>
                          <div>
                            <p className="font-semibold text-sm text-gray-800">{insight.category}</p>
                            <p className="text-sm text-gray-700 mt-1">{insight.message}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-6 border-t border-gray-200 bg-gray-50 flex justify-between items-center flex-shrink-0">
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowAdvancedResultModal(false);
                    setShowAdvancedModal(true);
                  }}
                  className="px-6 py-2 border-2 border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50 transition-colors font-semibold flex items-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  Edit Data
                </button>
                <button
                  onClick={() => setShowAdvancedResultModal(false)}
                  className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors font-semibold"
                >
                  Close
                </button>
              </div>
              <button
                onClick={() => {
                  alert('PDF export coming soon!');
                }}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Export Report
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Calculation Details Modal */}
      {showCalculationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[85vh] flex flex-col">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 p-6 flex justify-between items-center shadow-lg rounded-t-xl">
              <div className="flex items-center gap-3">
                <div className="bg-white/20 p-2 rounded-lg backdrop-blur-sm">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-white">Calculation Details</h3>
                  <p className="text-blue-100 text-sm">
                    {result && result.method === 'cca' && 'Comparable Company Analysis'}
                    {result && result.method === 'dcf' && 'Discounted Cash Flow'}
                    {result && result.method === 'capitalization' && 'Capitalization of Earnings'}
                    {result && result.method === 'nav' && 'Net Asset Value'}
                    {result && result.method === 'manual' && 'Manual Multiple'}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowCalculationModal(false)}
                className="text-white/80 hover:text-white transition-colors bg-white/10 hover:bg-white/20 rounded-lg p-2"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {result && (
                <div className="space-y-6">
                  {/* Summary Section */}
                  {result.results && result.results.summary && (
                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-lg border border-blue-200">
                      <h4 className="font-bold text-lg text-gray-900 mb-4">Median Value Summary</h4>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm text-gray-600">Methods Used</p>
                          <p className="text-lg font-semibold text-gray-900">{result.results.summary.methods_used}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Private Discount</p>
                          <p className="text-lg font-semibold text-gray-900">{result.results.summary.private_discount_applied || formData.private_company_discount}%</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Weighted Average</p>
                          <p className="text-lg font-semibold text-green-700">${result.results.summary.weighted_average?.toLocaleString()}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Simple Average</p>
                          <p className="text-lg font-semibold text-gray-900">${result.results.summary.simple_average?.toLocaleString()}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Single Method Calculation */}
                  {result.results && !result.results.methods && (
                    <div className="space-y-4">
                      {/* Wrap single method result to reuse the same rendering logic */}
                      {(() => {
                        const methodKey = result.method;
                        const methodData = result.results;
                        const methods = { [methodKey]: methodData };

                        return Object.entries(methods).map(([methodKey, methodData]) => {
                          const primaryValuation = methodData.valuations && Object.entries(methodData.valuations).find(
                            ([key, val]) => val.median === methodData.recommended || val === methodData.recommended
                          );

                          return (
                            <div key={methodKey} className="bg-white rounded-lg shadow-sm border border-gray-200">
                              {/* Content */}
                              <div className="p-6 space-y-6">

                                {/* DCF Formulas */}
                                {methodKey === 'dcf' && (
                                  <div className="space-y-4">
                                    {/* Input Parameters */}
                                    <div className="bg-gradient-to-br from-blue-100 to-indigo-100 p-5 rounded-lg border-2 border-blue-400 shadow-md">
                                      <p className="text-base font-bold text-blue-900 mb-4 flex items-center gap-2">
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                        </svg>
                                        Input Parameters
                                      </p>
                                      <div className="grid grid-cols-2 gap-3 text-sm">
                                        <div className="bg-white/70 p-2 rounded">
                                          <p className="text-xs text-gray-600 mb-1">Free Cash Flow (CF₀)</p>
                                          <p className="font-bold text-blue-900">${parseFloat(formData.cash_flow || 0).toLocaleString()}</p>
                                        </div>
                                        <div className="bg-white/70 p-2 rounded">
                                          <p className="text-xs text-gray-600 mb-1">Discount Rate (r)</p>
                                          <p className="font-bold text-blue-900">{(formData.dcf_discount_rate || 15)}%</p>
                                        </div>
                                        {methodData.details?.is_perpetuity ? (
                                          <>
                                            <div className="bg-white/70 p-2 rounded">
                                              <p className="text-xs text-gray-600 mb-1">Terminal Growth Rate (g)</p>
                                              <p className="font-bold text-blue-900">{(formData.dcf_growth_rate || 3)}%</p>
                                            </div>
                                            <div className="bg-white/70 p-2 rounded">
                                              <p className="text-xs text-gray-600 mb-1">Model Type</p>
                                              <p className="font-bold text-blue-900">Gordon Growth</p>
                                            </div>
                                          </>
                                        ) : (
                                          <>
                                            <div className="bg-white/70 p-2 rounded">
                                              <p className="text-xs text-gray-600 mb-1">Projection Period</p>
                                              <p className="font-bold text-blue-900">{methodData.details?.projection_years || formData.dcf_years || 5} years</p>
                                            </div>
                                            <div className="bg-white/70 p-2 rounded">
                                              <p className="text-xs text-gray-600 mb-1">Annual Growth Rate</p>
                                              <p className="font-bold text-blue-900">{(formData.dcf_annual_growth_rate || 5)}%</p>
                                            </div>
                                            <div className="bg-white/70 p-2 rounded">
                                              <p className="text-xs text-gray-600 mb-1">Terminal Growth Rate (g)</p>
                                              <p className="font-bold text-blue-900">{(formData.dcf_growth_rate || 3)}%</p>
                                            </div>
                                            <div className="bg-white/70 p-2 rounded">
                                              <p className="text-xs text-gray-600 mb-1">Model Type</p>
                                              <p className="font-bold text-blue-900">Multi-Period DCF</p>
                                            </div>
                                          </>
                                        )}
                                        <div className="bg-white/70 p-2 rounded col-span-2">
                                          <p className="text-xs text-gray-600 mb-1">Private Company Discount</p>
                                          <p className="font-bold text-amber-700">{formData.private_company_discount || 0}%</p>
                                        </div>
                                      </div>
                                    </div>

                                    {/* Gordon Growth Model Calculation */}
                                    {methodData.details?.is_perpetuity && methodData.details?.calculation_steps?.gordon_growth_calculation && (
                                      <div className="bg-white p-3 rounded border border-blue-100">
                                        <p className="text-sm font-bold text-blue-800 mb-3">Gordon Growth Model Calculation:</p>
                                        <div className="space-y-3 text-sm">
                                          <div>
                                            <p className="text-gray-700 font-semibold mb-1">Step 1: Calculate next year's cash flow (CF₁)</p>
                                            <p className="font-mono text-gray-900 bg-gray-50 p-2 rounded">
                                              CF₁ = CF₀ × (1 + g)<br />
                                              CF₁ = ${methodData.details.calculation_steps.gordon_growth_calculation.cf0?.toLocaleString()} × (1 + {((formData.dcf_growth_rate || 3) / 100).toFixed(4)})<br />
                                              <span className="text-blue-700 font-bold">CF₁ = ${methodData.details.calculation_steps.gordon_growth_calculation.cf1?.toLocaleString()}</span>
                                            </p>
                                          </div>

                                          <div>
                                            <p className="text-gray-700 font-semibold mb-1">Step 2: Calculate Enterprise Value</p>
                                            <p className="font-mono text-gray-900 bg-gray-50 p-2 rounded">
                                              Enterprise Value = CF₁ / (r - g)<br />
                                              Enterprise Value = ${methodData.details.calculation_steps.gordon_growth_calculation.cf1?.toLocaleString()} / ({((formData.dcf_discount_rate || 15) / 100).toFixed(4)} - {((formData.dcf_growth_rate || 3) / 100).toFixed(4)})<br />
                                              <span className="text-green-700 font-bold">Enterprise Value = ${methodData.details.calculation_steps.gordon_growth_calculation.enterprise_value?.toLocaleString()}</span>
                                            </p>
                                          </div>
                                        </div>
                                      </div>
                                    )}

                                    {/* Multi-Year Projection Calculations */}
                                    {!methodData.details?.is_perpetuity && methodData.details?.calculation_steps?.yearly_calculations && (
                                      <>
                                        {/* Step 1: Project Explicit Period FCFs */}
                                        <div className="bg-white p-3 rounded border border-blue-100">
                                          <p className="text-sm font-bold text-blue-800 mb-3">Step 1: Project Explicit Period FCFs (Years 1–{methodData.details.projection_years})</p>
                                          <p className="text-xs text-gray-700 mb-2">Formula for each year (input FCF represents Year 0/today):</p>
                                          <p className="text-xs font-mono text-gray-900 mb-3">FCF_t = FCF_0 × (1 + explicit growth)^t</p>
                                          <div className="space-y-2 text-sm">
                                            {methodData.details.calculation_steps.yearly_calculations.map((calc) => (
                                              <div key={calc.year} className="bg-gray-50 p-2 rounded">
                                                <p className="font-semibold text-gray-900">Year {calc.year}:</p>
                                                <p className="font-mono text-gray-700 text-xs">FCF_{calc.year} = ${methodData.details.calculation_steps.base_fcf?.toLocaleString()} × (1 + {(calc.growth_rate * 100).toFixed(0)}%)^{calc.exponent} = ${methodData.details.calculation_steps.base_fcf?.toLocaleString()} × {calc.growth_multiplier?.toFixed(6)} = <span className="font-bold">${Math.round(calc.cash_flow).toLocaleString()}</span></p>
                                              </div>
                                            ))}
                                          </div>
                                        </div>

                                        {/* Step 2: Calculate PV of Each Explicit FCF */}
                                        <div className="bg-white p-3 rounded border border-blue-100">
                                          <p className="text-sm font-bold text-blue-800 mb-3">Step 2: Calculate PV of Each Explicit FCF</p>
                                          <p className="text-xs text-gray-700 mb-2">Discount each FCF back to today using:</p>
                                          <p className="text-xs font-mono text-gray-900 mb-3">PV_t = FCF_t / (1 + r)^t</p>
                                          <div className="space-y-2 text-sm">
                                            {methodData.details.calculation_steps.yearly_calculations.map((calc) => (
                                              <div key={calc.year} className="bg-gray-50 p-2 rounded">
                                                <p className="font-semibold text-gray-900">Year {calc.year} PV:</p>
                                                <p className="font-mono text-gray-700 text-xs">PV_{calc.year} = ${Math.round(calc.cash_flow).toLocaleString()} / (1 + {(methodData.details.discount_rate * 100).toFixed(0)}%)^{calc.year} = ${Math.round(calc.cash_flow).toLocaleString()} / {calc.discount_factor?.toFixed(calc.year <= 2 ? 2 : 6)} = <span className="font-bold">${Math.round(calc.present_value).toLocaleString()}</span></p>
                                              </div>
                                            ))}
                                            <div className="bg-blue-50 p-3 rounded border border-blue-200 mt-3">
                                              <p className="font-bold text-blue-900 text-sm">Sum of PVs for Explicit Period:</p>
                                              <p className="font-mono text-blue-700 text-xs">
                                                {methodData.details.calculation_steps.yearly_calculations.map(calc => `$${Math.round(calc.present_value).toLocaleString()}`).join(' + ')} = <span className="font-bold">${Math.round(methodData.details?.calculation_steps?.final_calculation?.sum_of_pv_projections).toLocaleString()}</span>
                                              </p>
                                            </div>
                                          </div>
                                        </div>

                                        {/* Step 3: Calculate Terminal Value */}
                                        {methodData.details?.calculation_steps?.terminal_value_calculation && (
                                          <div className="bg-white p-3 rounded border border-blue-100">
                                            <p className="text-sm font-bold text-blue-800 mb-3">Step 3: Calculate Terminal Value (TV) at End of Year {methodData.details.projection_years}</p>
                                            <div className="space-y-3 text-sm">
                                              <div>
                                                <p className="text-gray-700 mb-1">First, project FCF for Year {methodData.details.projection_years + 1} (using terminal growth):</p>
                                                <p className="font-mono text-gray-900 bg-gray-50 p-2 rounded text-xs">
                                                  FCF_{methodData.details.projection_years + 1} = FCF_{methodData.details.projection_years} × (1 + g) = ${Math.round(methodData.details.calculation_steps.terminal_value_calculation.last_year_fcf).toLocaleString()} × (1 + {(methodData.details.terminal_growth * 100).toFixed(0)}%) = <span className="font-bold">${Math.round(methodData.details.calculation_steps.terminal_value_calculation.terminal_cash_flow).toLocaleString()}</span>
                                                </p>
                                              </div>
                                              <div>
                                                <p className="text-gray-700 mb-1">Then, apply Gordon Growth Model for perpetuity:</p>
                                                <p className="font-mono text-gray-900 bg-gray-50 p-2 rounded text-xs">
                                                  TV_{methodData.details.projection_years} = FCF_{methodData.details.projection_years + 1} / (r - g) = ${Math.round(methodData.details.calculation_steps.terminal_value_calculation.terminal_cash_flow).toLocaleString()} / ({(methodData.details.discount_rate * 100).toFixed(0)}% - {(methodData.details.terminal_growth * 100).toFixed(0)}%) = <span className="font-bold">${Math.round(methodData.details.calculation_steps.terminal_value_calculation.terminal_value).toLocaleString()}</span>
                                                </p>
                                              </div>
                                            </div>
                                          </div>
                                        )}

                                        {/* Step 4: Discount Terminal Value to Present */}
                                        {methodData.details?.calculation_steps?.terminal_value_calculation && (
                                          <div className="bg-white p-3 rounded border border-blue-100">
                                            <p className="text-sm font-bold text-blue-800 mb-3">Step 4: Discount Terminal Value to Present</p>
                                            <div className="text-sm">
                                              <p className="font-mono text-gray-900 bg-gray-50 p-2 rounded text-xs">
                                                PV_terminal = TV_{methodData.details.projection_years} / (1 + r)^{methodData.details.projection_years} = ${Math.round(methodData.details.calculation_steps.terminal_value_calculation.terminal_value).toLocaleString()} / (1 + {(methodData.details.discount_rate * 100).toFixed(0)}%)^{methodData.details.projection_years} = <span className="font-bold">${Math.round(methodData.details.calculation_steps.terminal_value_calculation.terminal_pv).toLocaleString()}</span>
                                              </p>
                                            </div>
                                          </div>
                                        )}

                                        {/* Step 5: Total Enterprise Value */}
                                        {methodData.details?.calculation_steps?.final_calculation && (
                                          <div className="bg-gradient-to-r from-green-50 to-blue-50 p-4 rounded-lg border-l-4 border-green-500 shadow-sm">
                                            <p className="text-sm font-bold text-green-800 mb-3 flex items-center gap-2">
                                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                              </svg>
                                              Step 5: Total Enterprise Value (EV)
                                            </p>
                                            <div className="space-y-2 text-sm">
                                              <p className="text-gray-700">Sum the PV of explicit period and PV of terminal:</p>
                                              <p className="font-mono text-gray-900 text-xs bg-white/70 p-2 rounded">
                                                EV = PV_explicit + PV_terminal<br />
                                                EV = ${Math.round(methodData.details.calculation_steps.final_calculation.sum_of_pv_projections).toLocaleString()} + ${Math.round(methodData.details.calculation_steps.final_calculation.pv_of_terminal).toLocaleString()}<br />
                                                <span className="font-bold text-green-700 text-base">EV = ${Math.round(methodData.details.calculation_steps.final_calculation.enterprise_value).toLocaleString()}</span>
                                              </p>
                                            </div>
                                          </div>
                                        )}
                                      </>
                                    )}

                                    {/* Private Company Discount Application */}
                                    {formData.private_company_discount > 0 && (
                                      <div className="bg-gradient-to-br from-amber-50 to-orange-50 p-4 rounded-lg border-l-4 border-amber-500 shadow-sm">
                                        <p className="text-sm font-bold text-amber-900 mb-3 flex items-center gap-2">
                                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                          </svg>
                                          Final Step: Apply Private Company Discount
                                        </p>
                                        <div className="space-y-2 text-sm">
                                          <p className="text-gray-700">Private companies typically trade at a discount to public companies due to illiquidity and information asymmetry.</p>
                                          <div className="bg-white/70 p-3 rounded">
                                            <p className="font-mono text-gray-900 text-xs">
                                              Base Enterprise Value = ${(methodData.details?.calculation_steps?.final_calculation?.enterprise_value || methodData.details?.calculation_steps?.gordon_growth_calculation?.enterprise_value || methodData.recommended / (1 - formData.private_company_discount / 100)).toLocaleString()}
                                            </p>
                                            <p className="font-mono text-amber-700 text-xs mt-2">
                                              Discount Factor = {formData.private_company_discount}% = {(formData.private_company_discount / 100).toFixed(3)}
                                            </p>
                                            <p className="font-mono text-amber-700 text-xs mt-1">
                                              Adjustment Multiplier = 1 - {(formData.private_company_discount / 100).toFixed(3)} = {(1 - formData.private_company_discount / 100).toFixed(3)}
                                            </p>
                                            <p className="font-mono text-gray-900 text-xs mt-2">
                                              Adjusted Value = ${(methodData.details?.calculation_steps?.final_calculation?.enterprise_value || methodData.details?.calculation_steps?.gordon_growth_calculation?.enterprise_value || methodData.recommended / (1 - formData.private_company_discount / 100)).toLocaleString()} × {(1 - formData.private_company_discount / 100).toFixed(3)}
                                            </p>
                                            <div className="mt-3 pt-3 border-t border-amber-200">
                                              <p className="font-bold text-green-700 text-lg">
                                                Median Value = ${methodData.recommended?.toLocaleString()}
                                              </p>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    )}

                                    {/* Fallback if no detailed steps available */}
                                    {!methodData.details?.calculation_steps && (
                                      <div className="space-y-2 font-mono text-sm bg-white p-3 rounded border border-blue-100">
                                        <p className="text-gray-700">Calculation:</p>
                                        <p className="text-gray-700">Multi-period DCF + Terminal Value:</p>
                                        <p className="text-gray-700 mt-2">PV = Σ [CF_t / (1 + r)^t] + [TV / (1 + r)^n]</p>
                                        <p className="text-gray-700">Where CF_t = projected cash flows for year t</p>
                                        <p className="text-gray-700">n = projection period = {methodData.details?.projection_years || formData.dcf_years || 5} years</p>
                                        <p className="text-gray-700">r = discount rate = {(formData.dcf_discount_rate || 15) / 100}</p>
                                        <p className="text-gray-700">TV = terminal value using g = {(formData.dcf_growth_rate || 3) / 100}</p>
                                        {methodData.recommended && (
                                          <p className="text-green-700 font-bold mt-2 text-base">
                                            Total PV: ${methodData.recommended.toLocaleString()}
                                          </p>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                )}

                                {/* CCA Formulas */}
                                {methodKey === 'cca' && methodData.valuations && (
                                  <div className="space-y-4">
                                    {/* Input Parameters */}
                                    <div className="bg-gradient-to-br from-blue-100 to-indigo-100 p-5 rounded-lg border-2 border-blue-400 shadow-md">
                                      <p className="text-base font-bold text-blue-900 mb-4 flex items-center gap-2">
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                        </svg>
                                        Input Parameters
                                      </p>
                                      <div className="grid grid-cols-2 gap-3 text-sm">
                                        <div className="bg-white/70 p-2 rounded">
                                          <p className="text-xs text-gray-600 mb-1">Primary Metric</p>
                                          <p className="font-bold text-blue-900">{formData.cca_multiple_type === 'ev_ebitda' || !formData.cca_multiple_type ? 'EBITDA' : 'Revenue'}</p>
                                        </div>
                                        <div className="bg-white/70 p-2 rounded">
                                          <p className="text-xs text-gray-600 mb-1">Multiple Type</p>
                                          <p className="font-bold text-blue-900">{formData.cca_multiple_type === 'ev_ebitda' || !formData.cca_multiple_type ? 'EV/EBITDA' : 'EV/Revenue'}</p>
                                        </div>
                                        <div className="bg-white/70 p-2 rounded">
                                          <p className="text-xs text-gray-600 mb-1">Revenue</p>
                                          <p className="font-bold text-blue-900">${parseFloat(formData.revenue || 0).toLocaleString()}</p>
                                        </div>
                                        <div className="bg-white/70 p-2 rounded">
                                          <p className="text-xs text-gray-600 mb-1">EBITDA</p>
                                          <p className="font-bold text-blue-900">${parseFloat(formData.ebitda || 0).toLocaleString()}</p>
                                        </div>
                                        <div className="bg-white/70 p-2 rounded col-span-2">
                                          <p className="text-xs text-gray-600 mb-1">Private Company Discount</p>
                                          <p className="font-bold text-amber-700">{formData.private_company_discount || 0}%</p>
                                        </div>
                                      </div>
                                    </div>

                                    {/* Multiple Calculations */}
                                    {Object.entries(methodData.valuations).map(([multipleType, vals], index) => {
                                      const isEVEBITDA = multipleType === 'ev_ebitda';
                                      const isEVSales = multipleType === 'ev_sales';
                                      const financialBase = isEVEBITDA ? formData.ebitda : formData.revenue;
                                      const multipleUsed = vals.multiple_used;
                                      const discount = formData.private_company_discount / 100;
                                      const baseValue = parseFloat(financialBase) * multipleUsed;
                                      const adjustedValue = baseValue * (1 - discount);

                                      return (
                                        <div key={multipleType} className="bg-white p-4 rounded-lg border border-blue-200 shadow-sm">
                                          <p className="text-sm font-bold text-blue-800 mb-3 flex items-center gap-2">
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                            </svg>
                                            Step {index + 1}: {multipleType === 'ev_sales' ? 'EV/REVENUE' : multipleType.replace(/_/g, '/').toUpperCase()} Valuation
                                          </p>

                                          <div className="space-y-3">
                                            {/* Industry Multiple */}
                                            <div className="bg-blue-50 p-3 rounded">
                                              <p className="text-xs text-gray-600 mb-1">Industry Multiple</p>
                                              <p className="font-mono text-blue-700 font-bold text-base">{multipleUsed?.toFixed(2)}x</p>
                                            </div>

                                            {/* Calculation Steps */}
                                            <div className="bg-gray-50 p-3 rounded space-y-2">
                                              <p className="text-xs font-semibold text-gray-700">Calculation:</p>

                                              {/* Step 1: Base Valuation */}
                                              <div className="font-mono text-xs">
                                                <p className="text-gray-700">Base Value = {isEVEBITDA ? 'EBITDA' : 'Revenue'} × Industry Multiple</p>
                                                <p className="text-gray-900 mt-1">
                                                  Base Value = ${parseFloat(financialBase).toLocaleString()} × {multipleUsed?.toFixed(2)}x
                                                </p>
                                                <p className="text-blue-700 font-bold mt-1">
                                                  Base Value = ${baseValue.toLocaleString()}
                                                </p>
                                              </div>

                                              {/* Step 2: Apply Discount if exists */}
                                              {discount > 0 && (
                                                <div className="mt-3 pt-3 border-t border-gray-200">
                                                  <div className="font-mono text-xs space-y-1">
                                                    <p className="text-amber-700 font-semibold">Apply Private Company Discount ({formData.private_company_discount}%):</p>
                                                    <p className="text-gray-700">
                                                      Adjustment Multiplier = 1 - {(discount).toFixed(3)} = {(1 - discount).toFixed(3)}
                                                    </p>
                                                    <p className="text-gray-900 mt-1">
                                                      Adjusted Value = ${baseValue.toLocaleString()} × {(1 - discount).toFixed(3)}
                                                    </p>
                                                    <p className="text-amber-700 font-bold mt-1">
                                                      Adjusted Value = ${adjustedValue.toLocaleString()}
                                                    </p>
                                                  </div>
                                                </div>
                                              )}
                                            </div>

                                            {/* Valuation Range */}
                                            <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-3 rounded-lg border-l-4 border-green-500">
                                              <div className="grid grid-cols-2 gap-3 text-sm">
                                                <div>
                                                  <p className="text-xs text-gray-600 mb-1">Valuation Range</p>
                                                  <p className="font-bold text-green-700">
                                                    ${vals.low?.toLocaleString()} - ${vals.high?.toLocaleString()}
                                                  </p>
                                                </div>
                                                <div>
                                                  <p className="text-xs text-gray-600 mb-1">Median Value</p>
                                                  <p className="font-bold text-blue-700 text-base">
                                                    ${vals.median?.toLocaleString()}
                                                  </p>
                                                </div>
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}

                                {/* Capitalization Formulas */}
                                {methodKey === 'capitalization' && (
                                  <div className="space-y-4">
                                    {/* Input Parameters */}
                                    <div className="bg-gradient-to-br from-purple-100 to-pink-100 p-5 rounded-lg border-2 border-purple-400 shadow-md">
                                      <p className="text-base font-bold text-purple-900 mb-4 flex items-center gap-2">
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                        </svg>
                                        Input Parameters
                                      </p>
                                      <div className="grid grid-cols-2 gap-3 text-sm">
                                        <div className="bg-white/70 p-2 rounded">
                                          <p className="text-xs text-gray-600 mb-1">Earnings Metric</p>
                                          <p className="font-bold text-purple-900">{formData.net_income ? 'Net Income' : 'EBITDA'}</p>
                                        </div>
                                        <div className="bg-white/70 p-2 rounded">
                                          <p className="text-xs text-gray-600 mb-1">Earnings Amount</p>
                                          <p className="font-bold text-purple-900">${parseFloat(formData.net_income || formData.ebitda || 0).toLocaleString()}</p>
                                        </div>
                                        <div className="bg-white/70 p-2 rounded">
                                          <p className="text-xs text-gray-600 mb-1">Capitalization Rate</p>
                                          <p className="font-bold text-purple-900">{formData.capitalization_rate ? formData.capitalization_rate + '%' : methodData.details?.cap_rate || 'Industry-Derived'}</p>
                                        </div>
                                        <div className="bg-white/70 p-2 rounded">
                                          <p className="text-xs text-gray-600 mb-1">Private Company Discount</p>
                                          <p className="font-bold text-amber-700">{formData.private_company_discount || 0}%</p>
                                        </div>
                                      </div>
                                    </div>

                                    {/* Step 1: Calculate Base Value */}
                                    <div className="bg-white p-4 rounded-lg border border-purple-200 shadow-sm">
                                      <p className="text-sm font-bold text-purple-800 mb-3 flex items-center gap-2">
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                        </svg>
                                        Step 1: Calculate Base Business Value
                                      </p>
                                      <div className="space-y-2 text-sm">
                                        <p className="text-gray-700">The capitalization of earnings method values a business based on its earning power:</p>
                                        <div className="bg-purple-50 p-3 rounded">
                                          <p className="font-mono text-gray-900 text-xs">
                                            Business Value = {formData.net_income ? 'Net Income' : 'EBITDA'} ÷ Cap Rate
                                          </p>
                                          <p className="font-mono text-gray-900 text-xs mt-2">
                                            Business Value = ${parseFloat(formData.net_income || formData.ebitda || 0).toLocaleString()} ÷ {formData.capitalization_rate ? (formData.capitalization_rate / 100).toFixed(3) : '[Cap Rate]'}
                                          </p>
                                          <p className="font-mono text-purple-700 font-bold text-xs mt-2">
                                            Base Value = ${Math.round(methodData.recommended / (1 - (formData.private_company_discount || 0) / 100)).toLocaleString()}
                                          </p>
                                        </div>
                                      </div>
                                    </div>

                                    {/* Step 2: Apply Private Company Discount */}
                                    {formData.private_company_discount > 0 && (
                                      <div className="bg-gradient-to-br from-amber-50 to-orange-50 p-4 rounded-lg border-l-4 border-amber-500 shadow-sm">
                                        <p className="text-sm font-bold text-amber-900 mb-3 flex items-center gap-2">
                                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                          </svg>
                                          Step 2: Apply Private Company Discount
                                        </p>
                                        <div className="space-y-2 text-sm">
                                          <p className="text-gray-700">Adjust for illiquidity and information asymmetry in private companies:</p>
                                          <div className="bg-white/70 p-3 rounded">
                                            <p className="font-mono text-gray-900 text-xs">
                                              Base Value = ${Math.round(methodData.recommended / (1 - formData.private_company_discount / 100)).toLocaleString()}
                                            </p>
                                            <p className="font-mono text-amber-700 text-xs mt-2">
                                              Discount Factor = {formData.private_company_discount}% = {(formData.private_company_discount / 100).toFixed(3)}
                                            </p>
                                            <p className="font-mono text-amber-700 text-xs mt-1">
                                              Adjustment Multiplier = 1 - {(formData.private_company_discount / 100).toFixed(3)} = {(1 - formData.private_company_discount / 100).toFixed(3)}
                                            </p>
                                            <p className="font-mono text-gray-900 text-xs mt-2">
                                              Adjusted Value = ${Math.round(methodData.recommended / (1 - formData.private_company_discount / 100)).toLocaleString()} × {(1 - formData.private_company_discount / 100).toFixed(3)}
                                            </p>
                                            <div className="mt-3 pt-3 border-t border-amber-200">
                                              <p className="font-bold text-green-700 text-lg">
                                                Median Value = ${methodData.recommended?.toLocaleString()}
                                              </p>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                )}

                                {/* NAV Formulas */}
                                {methodKey === 'nav' && (
                                  <div className="space-y-4">
                                    {/* Input Parameters */}
                                    <div className="bg-gradient-to-br from-teal-100 to-cyan-100 p-5 rounded-lg border-2 border-teal-400 shadow-md">
                                      <p className="text-base font-bold text-teal-900 mb-4 flex items-center gap-2">
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                        </svg>
                                        Input Parameters
                                      </p>
                                      <div className="grid grid-cols-2 gap-3 text-sm">
                                        <div className="bg-white/70 p-2 rounded">
                                          <p className="text-xs text-gray-600 mb-1">Total Assets</p>
                                          <p className="font-bold text-teal-900">${parseFloat(formData.total_assets || 0).toLocaleString()}</p>
                                        </div>
                                        <div className="bg-white/70 p-2 rounded">
                                          <p className="text-xs text-gray-600 mb-1">Total Liabilities</p>
                                          <p className="font-bold text-red-700">${parseFloat(formData.total_liabilities || 0).toLocaleString()}</p>
                                        </div>
                                        <div className="bg-white/70 p-2 rounded col-span-2">
                                          <p className="text-xs text-gray-600 mb-1">Private Company Discount</p>
                                          <p className="font-bold text-amber-700">{formData.private_company_discount || 0}%</p>
                                        </div>
                                      </div>
                                    </div>

                                    {/* Step 1: Calculate Net Asset Value */}
                                    <div className="bg-white p-4 rounded-lg border border-teal-200 shadow-sm">
                                      <p className="text-sm font-bold text-teal-800 mb-3 flex items-center gap-2">
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                        </svg>
                                        Step 1: Calculate Net Asset Value
                                      </p>
                                      <div className="space-y-2 text-sm">
                                        <p className="text-gray-700">The NAV method values a business based on the fair market value of its net assets:</p>
                                        <div className="bg-teal-50 p-3 rounded">
                                          <p className="font-mono text-gray-900 text-xs">
                                            Net Asset Value = Total Assets - Total Liabilities
                                          </p>
                                          <p className="font-mono text-gray-900 text-xs mt-2">
                                            NAV = ${parseFloat(formData.total_assets || 0).toLocaleString()} - ${parseFloat(formData.total_liabilities || 0).toLocaleString()}
                                          </p>
                                          <p className="font-mono text-teal-700 font-bold text-xs mt-2">
                                            Base NAV = ${Math.round(parseFloat(formData.total_assets || 0) - parseFloat(formData.total_liabilities || 0)).toLocaleString()}
                                          </p>
                                        </div>
                                      </div>
                                    </div>

                                    {/* Step 2: Apply Private Company Discount */}
                                    {formData.private_company_discount > 0 && (
                                      <div className="bg-gradient-to-br from-amber-50 to-orange-50 p-4 rounded-lg border-l-4 border-amber-500 shadow-sm">
                                        <p className="text-sm font-bold text-amber-900 mb-3 flex items-center gap-2">
                                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                          </svg>
                                          Step 2: Apply Private Company Discount
                                        </p>
                                        <div className="space-y-2 text-sm">
                                          <p className="text-gray-700">Adjust for marketability and liquidity constraints in private company assets:</p>
                                          <div className="bg-white/70 p-3 rounded">
                                            <p className="font-mono text-gray-900 text-xs">
                                              Base NAV = ${Math.round(parseFloat(formData.total_assets || 0) - parseFloat(formData.total_liabilities || 0)).toLocaleString()}
                                            </p>
                                            <p className="font-mono text-amber-700 text-xs mt-2">
                                              Discount Factor = {formData.private_company_discount}% = {(formData.private_company_discount / 100).toFixed(3)}
                                            </p>
                                            <p className="font-mono text-amber-700 text-xs mt-1">
                                              Adjustment Multiplier = 1 - {(formData.private_company_discount / 100).toFixed(3)} = {(1 - formData.private_company_discount / 100).toFixed(3)}
                                            </p>
                                            <p className="font-mono text-gray-900 text-xs mt-2">
                                              Adjusted NAV = ${Math.round(parseFloat(formData.total_assets || 0) - parseFloat(formData.total_liabilities || 0)).toLocaleString()} × {(1 - formData.private_company_discount / 100).toFixed(3)}
                                            </p>
                                            <div className="mt-3 pt-3 border-t border-amber-200">
                                              <p className="font-bold text-green-700 text-lg">
                                                Median Value = ${methodData.recommended?.toLocaleString()}
                                              </p>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                )}

                                {/* Manual Multiple Formulas */}
                                {methodKey === 'manual' && (
                                  <div className="space-y-4">
                                    {/* Input Parameters */}
                                    <div className="bg-gradient-to-br from-indigo-100 to-purple-100 p-5 rounded-lg border-2 border-indigo-400 shadow-md">
                                      <p className="text-base font-bold text-indigo-900 mb-4 flex items-center gap-2">
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                        </svg>
                                        Input Parameters
                                      </p>
                                      <div className="grid grid-cols-2 gap-3 text-sm">
                                        <div className="bg-white/70 p-2 rounded">
                                          <p className="text-xs text-gray-600 mb-1">Custom Multiple</p>
                                          <p className="font-bold text-indigo-900">{formData.manual_multiple}x</p>
                                        </div>
                                        <div className="bg-white/70 p-2 rounded">
                                          <p className="text-xs text-gray-600 mb-1">Multiple Type</p>
                                          <p className="font-bold text-indigo-900">{formData.manual_multiple_type === 'ev_ebitda' ? 'EV/EBITDA' : 'EV/Revenue'}</p>
                                        </div>
                                        <div className="bg-white/70 p-2 rounded">
                                          <p className="text-xs text-gray-600 mb-1">Financial Base</p>
                                          <p className="font-bold text-indigo-900">
                                            ${formData.manual_multiple_type === 'ev_ebitda'
                                              ? parseFloat(formData.ebitda || 0).toLocaleString()
                                              : parseFloat(formData.revenue || 0).toLocaleString()}
                                          </p>
                                        </div>
                                        <div className="bg-white/70 p-2 rounded">
                                          <p className="text-xs text-gray-600 mb-1">Private Company Discount</p>
                                          <p className="font-bold text-amber-700">{formData.private_company_discount || 0}%</p>
                                        </div>
                                      </div>
                                    </div>

                                    {/* Step 1: Calculate Base Value */}
                                    <div className="bg-white p-4 rounded-lg border border-indigo-200 shadow-sm">
                                      <p className="text-sm font-bold text-indigo-800 mb-3 flex items-center gap-2">
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                        </svg>
                                        Step 1: Calculate Base Business Value
                                      </p>
                                      <div className="space-y-2 text-sm">
                                        <p className="text-gray-700">Apply your custom multiple to the financial base:</p>
                                        <div className="bg-indigo-50 p-3 rounded">
                                          <p className="font-mono text-gray-900 text-xs">
                                            Business Value = {formData.manual_multiple_type === 'ev_ebitda' ? 'EBITDA' : 'Revenue'} × Custom Multiple
                                          </p>
                                          <p className="font-mono text-gray-900 text-xs mt-2">
                                            Business Value = ${formData.manual_multiple_type === 'ev_ebitda'
                                              ? parseFloat(formData.ebitda || 0).toLocaleString()
                                              : parseFloat(formData.revenue || 0).toLocaleString()} × {formData.manual_multiple}x
                                          </p>
                                          <p className="font-mono text-indigo-700 font-bold text-xs mt-2">
                                            Base Value = ${Math.round((formData.manual_multiple_type === 'ev_ebitda'
                                              ? parseFloat(formData.ebitda || 0)
                                              : parseFloat(formData.revenue || 0)) * parseFloat(formData.manual_multiple)).toLocaleString()}
                                          </p>
                                        </div>
                                      </div>
                                    </div>

                                    {/* Step 2: Apply Private Company Discount */}
                                    {formData.private_company_discount > 0 && (
                                      <div className="bg-gradient-to-br from-amber-50 to-orange-50 p-4 rounded-lg border-l-4 border-amber-500 shadow-sm">
                                        <p className="text-sm font-bold text-amber-900 mb-3 flex items-center gap-2">
                                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                          </svg>
                                          Step 2: Apply Private Company Discount
                                        </p>
                                        <div className="space-y-2 text-sm">
                                          <p className="text-gray-700">Adjust for private company illiquidity:</p>
                                          <div className="bg-white/70 p-3 rounded">
                                            <p className="font-mono text-gray-900 text-xs">
                                              Base Value = ${Math.round((formData.manual_multiple_type === 'ev_ebitda'
                                                ? parseFloat(formData.ebitda || 0)
                                                : parseFloat(formData.revenue || 0)) * parseFloat(formData.manual_multiple)).toLocaleString()}
                                            </p>
                                            <p className="font-mono text-amber-700 text-xs mt-2">
                                              Discount Factor = {formData.private_company_discount}% = {(formData.private_company_discount / 100).toFixed(3)}
                                            </p>
                                            <p className="font-mono text-amber-700 text-xs mt-1">
                                              Adjustment Multiplier = 1 - {(formData.private_company_discount / 100).toFixed(3)} = {(1 - formData.private_company_discount / 100).toFixed(3)}
                                            </p>
                                            <p className="font-mono text-gray-900 text-xs mt-2">
                                              Adjusted Value = ${Math.round((formData.manual_multiple_type === 'ev_ebitda'
                                                ? parseFloat(formData.ebitda || 0)
                                                : parseFloat(formData.revenue || 0)) * parseFloat(formData.manual_multiple)).toLocaleString()} × {(1 - formData.private_company_discount / 100).toFixed(3)}
                                            </p>
                                            <div className="mt-3 pt-3 border-t border-amber-200">
                                              <p className="font-bold text-green-700 text-lg">
                                                Median Value = ${methodData.recommended?.toLocaleString()}
                                              </p>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                      );
                    });
                  })()}
                </div>
              )}

              {/* Final Valuation Range */}
              <div className="bg-gradient-to-br from-green-500 via-emerald-500 to-teal-600 p-6 rounded-xl shadow-2xl">
                <div className="flex items-center gap-3 mb-4">
                  <div className="bg-white/20 p-2 rounded-lg backdrop-blur-sm">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h4 className="font-bold text-2xl text-white">Final Valuation</h4>
                </div>
                <div className="bg-white/95 backdrop-blur-sm rounded-lg p-5 space-y-3">
                  <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                    <span className="font-semibold text-gray-700">Valuation Range</span>
                    <span className="font-bold text-gray-900 text-xl">
                      ${result.low_range?.toLocaleString()} - ${result.high_range?.toLocaleString()}
                    </span>
                  </div>
                  {result.valuation_amount && (
                    <div className="flex justify-between items-center pt-2">
                      <span className="font-bold text-green-800 flex items-center gap-2">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        Median Business Value
                      </span>
                      <span className="font-bold text-green-700 text-2xl">
                        ${result.valuation_amount?.toLocaleString()}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

            {/* Modal Footer */}
            <div className="p-6 border-t border-gray-200 flex justify-end">
              <button
                onClick={() => setShowCalculationModal(false)}
                className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors font-semibold"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default ValuationCalculator;
